import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCheck, FileSignature, Lightbulb, Microscope, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { API, errorText } from '../governance_v2/constants';
import { CATEGORY_LABELS, STATUS_LABELS, metaFor } from './constants';
import { Breakdown, ScoreRing } from './ScoreParts';
import { INV_META, InvestigationDialog } from './InvestigationDialog';

const useStatus = (companyId, id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (status) => (await axios.patch(`${API}/companies/${companyId}/materiality/signals/${id}`, { status })).data,
    onSuccess: (_, status) => { toast.success(`Marcada como ${STATUS_LABELS[status].toLowerCase()}`); qc.invalidateQueries({ queryKey: ['materiality'] }); },
    onError: (e) => toast.error(errorText(e)),
  });
};

const InvestigationButton = ({ s, companyId, isAdmin }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const start = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/materiality/signals/${s.id}/investigate`),
    onSuccess: () => { toast.success('Investigación autónoma en cola'); qc.invalidateQueries({ queryKey: ['materiality'] }); },
    onError: (e) => toast.error(errorText(e)),
  });
  const canStart = isAdmin && !s.investigation && s.level === 'critica' && ['nueva', 'reconocida'].includes(s.status);
  if (!s.investigation && !canStart) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => (s.investigation ? setOpen(true) : start.mutate())} disabled={start.isPending}
        data-testid={`mat-investigation-${s.id}`} className="border-violet-400 text-violet-700 dark:text-violet-300"><Microscope className="mr-1 h-3.5 w-3.5" />
        {s.investigation ? INV_META[s.investigation.status]?.label : 'Investigar'}</Button>
      {open && <InvestigationDialog companyId={companyId} signal={s} isAdmin={isAdmin} onClose={() => setOpen(false)} />}
    </>
  );
};

const Actions = ({ s, companyId, isAdmin, onPropose }) => {
  const st = useStatus(companyId, s.id);
  const btn = (status, Icon, label) => (
    <Button size="sm" variant="ghost" onClick={() => st.mutate(status)} disabled={st.isPending} data-testid={`mat-${status}-${s.id}`}>
      <Icon className="mr-1 h-3.5 w-3.5" />{label}</Button>);
  const critical = s.kind === 'alert' && s.level === 'critica';
  return (
    <div className="flex flex-wrap items-center gap-1">
      {s.kind === 'alert' && isAdmin && !s.proposal_id && s.proposal_draft && ['nueva', 'reconocida'].includes(s.status) && (
        <Button size="sm" variant={critical ? 'default' : 'outline'} onClick={() => onPropose(s)} data-testid={`mat-propose-${s.id}`}
          className={critical ? 'bg-red-600 text-white on-solid hover:bg-red-700' : ''}><FileSignature className="mr-1 h-3.5 w-3.5" />Crear propuesta</Button>)}
      <InvestigationButton s={s} companyId={companyId} isAdmin={isAdmin} />
      {s.proposal_id && <Badge variant="outline" className="border-emerald-500 text-emerald-700 dark:text-emerald-400" data-testid={`mat-proposal-linked-${s.id}`}>Propuesta creada</Badge>}
      {s.status === 'nueva' && btn('reconocida', Check, 'Reconocer')}
      {isAdmin && ['nueva', 'reconocida'].includes(s.status) && btn('resuelta', CheckCheck, 'Resolver')}
      {isAdmin && ['nueva', 'reconocida'].includes(s.status) && btn('descartada', X, 'Descartar')}
      {isAdmin && ['descartada', 'resuelta'].includes(s.status) && btn('nueva', AlertTriangle, 'Reabrir')}
    </div>
  );
};

export const SignalCard = ({ s, companyId, isAdmin, onPropose, index }) => {
  const meta = metaFor(s);
  const Icon = s.kind === 'alert' ? AlertTriangle : Lightbulb;
  return (
    <article className="mat-reveal mat-card group rounded-2xl border border-slate-200 bg-white p-4 transition-shadow duration-200 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms`, borderLeft: `4px solid ${meta.ring}` }} data-testid={`mat-signal-${s.id}`}>
      <div className="flex gap-4">
        <ScoreRing score={s.score} meta={meta} testid={`mat-score-${s.id}`} />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Icon className={`h-4 w-4 ${s.kind === 'alert' ? 'text-red-500' : 'text-emerald-500'}`} />
            <Badge className={meta.cls} data-testid={`mat-level-${s.id}`}>{s.kind === 'alert' ? meta.label : `Impacto ${meta.adj}`}</Badge>
            <span className="text-xs text-slate-500">{CATEGORY_LABELS[s.category] || s.category}</span>
            <Badge variant="outline" className="ml-auto text-[10px]" data-testid={`mat-status-${s.id}`}>{STATUS_LABELS[s.status]}</Badge>
          </div>
          <h4 className="font-semibold leading-snug text-slate-900 dark:text-white" data-testid={`mat-title-${s.id}`}>{s.title}</h4>
          <p className="text-sm text-slate-600 dark:text-slate-300">{s.detail}</p>
          <Breakdown b={s.score_breakdown} meta={meta} testid={`mat-breakdown-${s.id}`} />
          <Actions s={s} companyId={companyId} isAdmin={isAdmin} onPropose={onPropose} />
        </div>
      </div>
    </article>
  );
};
